import { supabase } from '../supabaseClient';
import {
  normalizeGradeTrackProgramPreferences,
  type GradeTrackProgramPreferences,
} from './sectionLayoutService';
import type { TrackProgramMode } from '../utils/trackProgramModeStorage';
import { invalidateSchoolScopedDataCaches } from '../utils/schoolDataCacheVersion';

export interface LeaderPreviewTrackRow {
  name: string;
  description: string;
  criteria_hours: number;
  track_category: string;
}

export interface LeaderPreviewTrackMeta {
  program_mode: TrackProgramMode;
  sync_rep?: string;
  wildcard_char?: string;
}

export interface LeaderPreviewGradeSlot {
  label: string;
  grade_id?: string;
  tracks: LeaderPreviewTrackRow[];
  track_meta: Record<string, LeaderPreviewTrackMeta>;
}

export interface LeaderPreviewTrackColumnMeta {
  name: string;
  wildcardChar?: string;
}

export interface TeamLeaderGradeTrackSectionPreviewRow {
  section_id: string;
  grade_labels: string[];
  track_row_count: number;
  grade_slots: LeaderPreviewGradeSlot[];
  track_columns: LeaderPreviewTrackColumnMeta[];
  detail_loaded: boolean;
}

export interface TeamLeaderGradeTrackPreviewPayload {
  leader_workspace_id: string;
  program_preferences: GradeTrackProgramPreferences;
  sections: TeamLeaderGradeTrackSectionPreviewRow[];
}

function normalizeGradeIdKey(gradeId: string): string {
  return gradeId.trim().toLowerCase();
}

function prefsModeForTrack(
  prefs: GradeTrackProgramPreferences,
  gradeId: string | undefined,
  trackName: string
): TrackProgramMode | undefined {
  const key = trackName.trim();
  if (!gradeId || !key) return undefined;
  const gid = normalizeGradeIdKey(gradeId);
  const direct = prefs.modes[gradeId]?.[key] ?? prefs.modes[gid]?.[key];
  if (direct === 'unified' || direct === 'split' || direct === 'sync') return direct;
  for (const [storedGradeId, trackMap] of Object.entries(prefs.modes)) {
    if (normalizeGradeIdKey(storedGradeId) !== gid) continue;
    const mode = trackMap[key];
    if (mode === 'unified' || mode === 'split' || mode === 'sync') return mode;
  }
  return undefined;
}

function prefsSyncRepForTrack(
  prefs: GradeTrackProgramPreferences,
  gradeId: string | undefined,
  trackName: string
): string | undefined {
  const key = trackName.trim();
  if (!gradeId || !key) return undefined;
  const gid = normalizeGradeIdKey(gradeId);
  const direct = prefs.syncReps[gradeId]?.[key] ?? prefs.syncReps[gid]?.[key];
  if (direct) return direct;
  for (const [storedGradeId, trackMap] of Object.entries(prefs.syncReps)) {
    if (normalizeGradeIdKey(storedGradeId) !== gid) continue;
    const rep = trackMap[key];
    if (rep) return rep;
  }
  return undefined;
}

export function resolveLeaderPreviewTrackMeta(
  trackName: string,
  aliases: string[],
  options: {
    slotMeta?: LeaderPreviewTrackMeta;
    trackColumns: LeaderPreviewTrackColumnMeta[];
    programPreferences: GradeTrackProgramPreferences;
    gradeId?: string;
  }
): { programMode: TrackProgramMode; syncRepAlias: string; wildcardChar: string } {
  const key = trackName.trim();
  const fromSlot = options.slotMeta;
  const programMode =
    fromSlot?.program_mode ??
    prefsModeForTrack(options.programPreferences, options.gradeId, key) ??
    'unified';
  const savedRep = fromSlot?.sync_rep ?? prefsSyncRepForTrack(options.programPreferences, options.gradeId, key);
  const syncRepAlias =
    savedRep && aliases.includes(savedRep) ? savedRep : (aliases[0] ?? '');
  const wildcardFromSlot = fromSlot?.wildcard_char ?? '';
  const wildcardFromColumn =
    options.trackColumns.find((c) => c.name.trim() === key)?.wildcardChar ?? '';
  const wildcardChar = wildcardFromSlot || wildcardFromColumn;
  return { programMode, syncRepAlias, wildcardChar };
}

function buildTrackMetaFallback(
  slot: LeaderPreviewGradeSlot,
  trackColumns: LeaderPreviewTrackColumnMeta[],
  programPreferences: GradeTrackProgramPreferences
): Record<string, LeaderPreviewTrackMeta> {
  const out: Record<string, LeaderPreviewTrackMeta> = {};
  const names = new Set<string>();
  slot.tracks.forEach((track) => {
    const name = track.name.trim();
    if (name) names.add(name);
  });
  names.forEach((trackName) => {
    const aliases = slot.tracks
      .filter((track) => track.name.trim() === trackName)
      .map((track) => track.description.trim() || trackName);
    const uniqueAliases = aliases.filter((alias, index) => aliases.indexOf(alias) === index);
    const resolved = resolveLeaderPreviewTrackMeta(trackName, uniqueAliases, {
      trackColumns,
      programPreferences,
      gradeId: slot.grade_id,
    });
    out[trackName] = {
      program_mode: resolved.programMode,
      sync_rep: resolved.syncRepAlias,
      wildcard_char: resolved.wildcardChar || undefined,
    };
  });
  return out;
}

function parseTrackMeta(raw: unknown): Record<string, LeaderPreviewTrackMeta> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, LeaderPreviewTrackMeta> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([trackName, value]) => {
    if (!trackName.trim() || !value || typeof value !== 'object' || Array.isArray(value)) return;
    const o = value as Record<string, unknown>;
    const modeRaw = String(o.program_mode ?? 'unified');
    const program_mode: TrackProgramMode =
      modeRaw === 'split' || modeRaw === 'sync' ? modeRaw : 'unified';
    const sync_rep = typeof o.sync_rep === 'string' && o.sync_rep.trim() ? o.sync_rep.trim() : undefined;
    const wildcardRaw = typeof o.wildcard_char === 'string' ? o.wildcard_char.trim() : '';
    const wildcard_char = wildcardRaw.match(/[가-힣]/)?.[0] ?? undefined;
    out[trackName.trim()] = {
      program_mode,
      sync_rep,
      wildcard_char,
    };
  });
  return out;
}

function parseGradeSlots(raw: unknown): LeaderPreviewGradeSlot[] {
  if (!Array.isArray(raw)) return [];
  const out: LeaderPreviewGradeSlot[] = [];
  for (const slot of raw) {
    if (!slot || typeof slot !== 'object') continue;
    const s = slot as Record<string, unknown>;
    const label = String(s.label ?? '');
    const grade_id = typeof s.grade_id === 'string' && s.grade_id.trim() ? s.grade_id.trim() : undefined;
    const tracksRaw = s.tracks;
    const tracks: LeaderPreviewTrackRow[] = [];
    if (Array.isArray(tracksRaw)) {
      for (const tr of tracksRaw) {
        if (!tr || typeof tr !== 'object') continue;
        const t = tr as Record<string, unknown>;
        tracks.push({
          name: String(t.name ?? ''),
          description: String(t.description ?? ''),
          criteria_hours: Number(t.criteria_hours ?? 0) || 0,
          track_category: String(t.track_category ?? 'regular'),
        });
      }
    }
    out.push({ label, grade_id, tracks, track_meta: parseTrackMeta(s.track_meta) });
  }
  return out;
}

function parseTrackColumns(raw: unknown): LeaderPreviewTrackColumnMeta[] {
  if (!Array.isArray(raw)) return [];
  const out: LeaderPreviewTrackColumnMeta[] = [];
  for (const col of raw) {
    if (!col || typeof col !== 'object') continue;
    const c = col as Record<string, unknown>;
    const name = String(c.name ?? '').trim();
    if (!name) continue;
    const wildcardRaw = typeof c.wildcardChar === 'string' ? c.wildcardChar.trim() : '';
    const wildcardChar = wildcardRaw.match(/[가-힣]/)?.[0] ?? '';
    out.push({
      name,
      wildcardChar: wildcardChar || undefined,
    });
  }
  return out;
}

export async function getTeamLeaderGradeTrackSectionsPreview(teamId: string): Promise<TeamLeaderGradeTrackPreviewPayload> {
  const { data, error } = await supabase.rpc('get_team_leader_grade_track_sections_preview', {
    p_team_id: teamId,
  });
  if (error) throw new Error(error.message);
  const raw = data as Record<string, unknown> | null;
  if (!raw || typeof raw !== 'object') {
    return {
      leader_workspace_id: '',
      program_preferences: { modes: {}, syncReps: {}, hoursAdjust: {}, learningPublishers: {} },
      sections: [],
    };
  }
  const leader_workspace_id = String(raw.leader_workspace_id ?? '');
  const program_preferences = normalizeGradeTrackProgramPreferences(raw.program_preferences);
  const sectionsRaw = raw.sections;
  const sections: TeamLeaderGradeTrackSectionPreviewRow[] = [];
  if (Array.isArray(sectionsRaw)) {
    for (const row of sectionsRaw) {
      if (!row || typeof row !== 'object') continue;
      const o = row as Record<string, unknown>;
      const section_id = String(o.section_id ?? '');
      const gl = o.grade_labels;
      const grade_labels = Array.isArray(gl) ? gl.map((x) => String(x)) : [];
      const track_row_count = Number(o.track_row_count ?? 0) || 0;
      const detail_loaded = Array.isArray(o.grade_slots);
      const grade_slots = parseGradeSlots(o.grade_slots);
      const track_columns = parseTrackColumns(o.track_columns);
      const enrichedSlots = grade_slots.map((slot) => ({
        ...slot,
        track_meta:
          Object.keys(slot.track_meta).length > 0
            ? slot.track_meta
            : buildTrackMetaFallback(slot, track_columns, program_preferences),
      }));
      sections.push({
        section_id,
        grade_labels,
        track_row_count,
        grade_slots: enrichedSlots,
        track_columns,
        detail_loaded,
      });
    }
  }
  return { leader_workspace_id, program_preferences, sections };
}

export async function importTeamLeaderGradeTrackSection(
  teamId: string,
  targetWorkspaceId: string,
  sourceSectionId: string,
  targetSectionId: string
): Promise<void> {
  const { error } = await supabase.rpc('import_team_grade_track_section', {
    p_team_id: teamId,
    p_target_workspace_id: targetWorkspaceId,
    p_source_section_id: sourceSectionId,
    p_target_section_id: targetSectionId,
  });
  if (error) throw new Error(error.message);
  await invalidateSchoolScopedDataCaches(targetWorkspaceId);
}
